import { useState, useEffect, useRef, useCallback, memo } from "react";
import { Overlay, OverlayTrigger, Popover } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import TaskModal from "../utils/TaskModal";
import LastActivityDisplay from "../utils/LastActivityDisplay";
import { getStickyStyle } from "../utils/stickyColumn";


// Only one activity popover may be open at a time across all rows; React's mouseleave is unreliable
// for content rendered in a portal, so a row that fails to hear its own leave is closed by the next one opening.
let hideActivePopover = null;

// Instant popover explaining why a drag handle is disabled (the native title tooltip is too slow)
const HandleHint = ({ hint, children }) => !hint ? children : (
  <OverlayTrigger placement="right" delay={0} overlay={<Popover style={{pointerEvents: "none"}}><Popover.Body className="py-1 px-2" style={{fontSize: "0.75rem"}}>{hint}</Popover.Body></Popover>}>
    {children}
  </OverlayTrigger>
);

// Reorder props (all optional; only My Tasks passes them):
//   reorderAvailable - the list supports reordering starred tasks (reserves room for the handle)
//   reorderEnabled   - reordering is usable right now (no column sort active, no save pending)
//   reorderHint      - why reordering is unavailable right now (shown in a popover on the lightened, disabled handles)
//   dragInProgress / isDragging / isDropTarget - drag state for this table / this row
const TaskListRow = ({row, showModal, reloadTaskLists, reorderAvailable=false, reorderEnabled=false, reorderHint=null, dragInProgress=false, isDragging=false, isDropTarget=false, onRowDragStart, onRowDragOver, onRowDrop, onRowDragEnd}) => {
  const [show, setShow] = useState(showModal);
  // The cell the activity popover is anchored to; null when hidden.
  const [popoverTarget, setPopoverTarget] = useState(null);
  const popoverTimer = useRef(null);

  const clearPopoverTimer = () => {
    if (popoverTimer.current) {
      clearTimeout(popoverTimer.current);
      popoverTimer.current = null;
    }
  };

  const hidePopover = useCallback(() => {
    clearPopoverTimer();
    setPopoverTarget(null);
  }, []);

  const showPopoverFor = (target) => {
    clearPopoverTimer();
    popoverTimer.current = setTimeout(() => {
      if (hideActivePopover && hideActivePopover !== hidePopover) {
        hideActivePopover();
      }
      hideActivePopover = hidePopover;
      setPopoverTarget(target);
    }, 100);
  };

  useEffect(() => () => {
    clearPopoverTimer();
    if (hideActivePopover === hidePopover) {
      hideActivePopover = null;
    }
  }, [hidePopover]);

  useEffect(() => {
    if (show) {
      hidePopover();
    }
  }, [show, hidePopover]);

  // A refresh replaces the task object (and may re-sort rows under a
  // stationary pointer, so no mouse event follows); dismiss rather than leave
  // the popover anchored to stale content.
  const task = row.original.task;
  useEffect(() => {
    hidePopover();
  }, [task, hidePopover]);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (showModal && searchParams.has('task'))
    {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('task');
      navigate({ search: newParams.toString() }, { replace: true });
    }
  }, [showModal, searchParams, navigate]);

  const handleShow =  (cellId) => 
  {
    if (cellId === "otherActions" || cellId === "starred")
    {
      return;
    }

    setShow(true);
  }

  const handleCancel = () => {
    setShow(false);
  }

  const handleSave = () => {
    setShow(false);
    reloadTaskLists();
  }

  const handleRefresh = () => {
    reloadTaskLists();
  }

  const handleDelete = () => {
    setShow(false);
    reloadTaskLists();
  }

  const canDragRow = reorderEnabled && task.starred;
  const dropStyle = isDropTarget ? {borderTop: "3px solid #3AAFA9"} : {};

  // Only the handle is draggable, so clicking and selecting text in the rest of the row still work.
  const handleDragStart = (e) => {
    hidePopover();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(task.id)); // Firefox won't start a drag without data
    const rowElement = e.currentTarget.closest("tr");
    if (rowElement) {
      e.dataTransfer.setDragImage(rowElement, 0, 0);
    }
    onRowDragStart(task.id);
  };

  // Only starred rows accept a drop
  const handleRowDragOver = (e) => {
    if (dragInProgress && !isDragging && task.starred) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      onRowDragOver(task.id);
    }
  };

  const handleRowDrop = (e) => {
    if (dragInProgress && task.starred) {
      e.preventDefault();
      onRowDrop(task.id);
    }
  };

  const handleHint = !canDragRow && task.starred && reorderHint;
  const dragHandle = !reorderAvailable ? null : (
    <HandleHint hint={handleHint}><span
      draggable={canDragRow}
      onDragStart={canDragRow ? handleDragStart : undefined}
      onDragEnd={canDragRow ? onRowDragEnd : undefined}
      title={canDragRow ? "Drag to reorder" : undefined}
      style={{display: "inline-block", width: "12px", textAlign: "center", cursor: canDragRow ? "grab" : "default", color: canDragRow ? "#9AA0A2" : "#D5D9DA", visibility: task.starred ? "visible" : "hidden"}}
    >
      <FontAwesomeIcon icon={faGripVertical} />
    </span></HandleHint>
  );

  return (
    <>         
      <tr {...row.getRowProps()} style={{cursor: "pointer", opacity: isDragging ? 0.4 : 1}} onMouseLeave={hidePopover} onDragOver={handleRowDragOver} onDrop={handleRowDrop}>
        {row.cells.map((cell) => {
          if (cell.column.key === "otherActions" || cell.column.key === "starred")
          {
            return (
              <td style={{cursor:"auto", ...getStickyStyle(cell.column, false), ...dropStyle}} onMouseEnter={hidePopover} onClick={(e) => { hidePopover(); handleShow(cell.column.key); }} className={(cell.column.key === "content" || cell.column.key === "created" || cell.column.key === "createdBy" || cell.column.key === "toNumber") && row.id.indexOf(".") > 0 ? "table-secondary" : ""} {...cell.getCellProps()}>
                  {cell.column.key === "starred" && reorderAvailable ?
                    <div className="d-flex align-items-center justify-content-center" style={{gap: "4px"}}>{dragHandle}{cell.render("Cell")}</div>
                    :
                    cell.render("Cell")
                  }
              </td>
            );
          }

          return (
            <td style={{...getStickyStyle(cell.column, false), ...dropStyle}} onMouseEnter={(e) => showPopoverFor(e.currentTarget)} onClick={(e) => handleShow(cell.column.key)} className={(cell.column.key === "content" || cell.column.key === "created" || cell.column.key === "createdBy" || cell.column.key === "toNumber") && row.id.indexOf(".") > 0 ? "table-secondary" : ""} {...cell.getCellProps()}>
              {cell.render("Cell")}
            </td>
          );
        })}
      </tr>
      <Overlay target={popoverTarget} show={popoverTarget !== null} placement="bottom" transition={false}>
        {(props) => (
          <Popover id={`task-activity-${row.original.task.id}`} {...props} style={{...props.style, pointerEvents: "none"}}>
            <Popover.Body><LastActivityDisplay task={row.original.task}></LastActivityDisplay></Popover.Body>
          </Popover>
        )}
      </Overlay>
      <TaskModal key={row.original.task.id} show={show} onCancel={handleCancel} onRefresh={handleRefresh} onSave={handleSave} onDelete={handleDelete} id={row.original.task.id} category={row.original.task.category.value} psvType={row.original.task.psvType} state={row.original.task.state}
      practiceId={row.original.task.practiceId} carrierId={row.original.task.carrierId} providerId={row.original.task.providerIsDeleted ? 0 : row.original.task.providerId} hubId={row.original.task.hubId} 
      facilityId={row.original.task.facilityId} other={row.original.task.other} referenceNumber={row.original.task.referenceNumber} assignees={row.original.task.assignees} status={row.original.task.status} 
      due={row.original.task.due} deadline={row.original.task.deadline} archived={row.original.task.archived} tracked={row.original.task.tracked} activityList={row.original.task.activity}
      eSignatureTokenInfo={row.original.task.eSignatureTokenInfo} documentTokenInfo={row.original.task.documentTokenInfo}/>
    </>
    )
}

// react-table rebuilds the `row` wrapper object on every render even when the
// underlying task hasn't changed, so compare the actual task reference rather
// than the wrapper to let unaffected rows skip re-rendering.
const arePropsEqual = (prevProps, nextProps) => {
  return prevProps.row.original.task === nextProps.row.original.task &&
    prevProps.showModal === nextProps.showModal &&
    prevProps.reloadTaskLists === nextProps.reloadTaskLists &&
    prevProps.reorderAvailable === nextProps.reorderAvailable &&
    prevProps.reorderEnabled === nextProps.reorderEnabled &&
    prevProps.reorderHint === nextProps.reorderHint &&
    prevProps.dragInProgress === nextProps.dragInProgress &&
    prevProps.isDragging === nextProps.isDragging &&
    prevProps.isDropTarget === nextProps.isDropTarget;
};

export default memo(TaskListRow, arePropsEqual);